"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Toggle as TogglePrimitive } from "radix-ui"

// The segmented control: a muted track with raised segments. ToggleGroup
// (variant="segmented") and Tabs (variant="segmented") both use it. The track
// sets the height and segments fill it, so the two stay the same size. The
// active segment gets a border as well as the card fill, because card on
// muted is nearly invisible in dark mode.
const segmentedStyles = {
  list: "inline-flex w-fit items-center gap-0.5 rounded-md bg-muted p-0.5",
  listSize: {
    sm: "h-control-sm",
    md: "h-control-md",
    lg: "h-control-lg",
  },
  item: "h-full rounded-sm border border-transparent bg-transparent px-2.5 hover:bg-transparent hover:text-foreground",
  // The selected segment's white fill on the muted track measures 1.2:1, so
  // its --input edge (3.52:1 on grey) is what marks the selection.
  active:
    "data-[state=active]:border-input data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-raised data-[state=on]:border-input data-[state=on]:bg-card data-[state=on]:text-foreground data-[state=on]:shadow-raised",
}

// The pill control: a white rounded-full track with a thin hairline border
// and a solid black fill on the selected segment — the dashboard's Team/
// period filters, kept at their pre-@yllw/ui look (1ef6368) rather than
// bent onto the segmented control's bordered-chip selection style.
const pillStyles = {
  list: "inline-flex w-fit items-center gap-0.5 rounded-full border border-border bg-card p-0.5",
  listSize: {
    sm: "h-control-sm",
    md: "h-control-md",
    lg: "h-control-lg",
  },
  item: "h-full rounded-full bg-transparent hover:bg-transparent",
  active:
    "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:hover:text-primary-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:hover:text-primary-foreground",
}

const toggleVariants = cva(
  "inline-flex items-center justify-center gap-1.5 rounded-md border border-transparent text-ui font-medium whitespace-nowrap text-muted-foreground focus-ring transition-[color,background-color,border-color] duration-fast ease-standard hover:bg-secondary-hover hover:text-foreground disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive data-[state=on]:text-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 data-[state=on]:[&_svg]:text-primary [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-transparent data-[state=on]:border-border-strong data-[state=on]:bg-accent",
        // Same reasoning as Button's outline variant: the general UI border
        // colour at rest (a toggle's own label identifies it, so it doesn't
        // need --input's stronger contrast), stepping up to --input on
        // hover and --border-strong once selected.
        outline:
          "border-border bg-card hover:border-input hover:bg-secondary-hover data-[state=on]:border-border-strong data-[state=on]:bg-accent",
        segmented: cn(segmentedStyles.item, segmentedStyles.active),
        pill: cn(pillStyles.item, pillStyles.active),
      },
      size: {
        sm: "h-control-sm min-w-control-sm px-1.5",
        md: "h-control-md min-w-control-md px-2",
        lg: "h-control-lg min-w-control-lg px-2.5",
        /** @deprecated Use `md`. */
        default: "h-control-md min-w-control-md px-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
)

function Toggle({
  className,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof TogglePrimitive.Root> &
  VariantProps<typeof toggleVariants>) {
  return (
    <TogglePrimitive.Root
      data-slot="toggle"
      className={cn(toggleVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Toggle, pillStyles, segmentedStyles, toggleVariants }
